import { roomEdgeHandleFromWallSide } from "building-common/walls/wall";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import { LUMBER_UNION_GAP, type LumberProfileDescriptor, type LumberRail } from "building-types/framing/lumberTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { TargetedOpeningSettings } from "building-types/openings/opening";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import { isOpeningHeaderEnabled } from "building-types/settings/openingHeaderAssemblySettingsTools";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";
import type { WallFramingSettings } from "building-types/settings/wallFramingSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { AdjacentRooms, RoughOpening } from "building-types/walls/wallFramingTypes";
import { DEFAULT_STUD_DISTANCE, MIN_FRAMING_STUD_LENGTH, WallFramingType } from "building-types/walls/wallFramingTypes";
import { OpeningSource, type WallFraming, type Framing } from "building-types/walls/wallFramingTypes";
import type { WallFramingMeta } from "building-types/walls/wallFramingTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { WallCondition } from "building-types/walls/wallType";
import type { SolidWallSide } from "building-types/walls/wallTypes";
import type { SolidWall } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { getLumberProfile, lumberRailNew } from "doc/utils/lumberTools";
import type { NodeBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, Line2Side, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { line2PerpDistance, line2Side } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planeNewXY } from "geom/plane";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeEval,
  polyLine2EdgeTangent,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2Length,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ByArray, polyLine3OnPlane } from "geom/polyLine3";
import { rangeInclusiveByStep } from "geom/range";
import { ray2DotProject } from "geom/ray2";
import { vec2DistSquared, vec2Dot } from "geom/vec2";
import { getWallAssemblyStructuralThickness } from "../wallAssemblies/wallAssembly";
import { getWallGaps } from "./solidWallCrossSectionUtils";
import { getWallPath } from "./wall";
import { addWallFramingMember, makeRoughOpening } from "./wallFramingUtils";
import { solidWallFramingLayer } from "./wallLayers";
import { wallSideCopy } from "./wallSideBasics";

const BLOCKING_MIN_DISTANCE = 1.5;
interface MakeWallFramingOptions {
  readonly level: Level;
  readonly wallSystem: WallSystem;
  readonly planarGraphPerimeter: PlanarGraphPerimeterInfo;
  readonly wallFramingSettings: WallFramingSettings;
  readonly wallCatalog: WallCatalogSettings;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly headerFramingSettings: HeaderFramingSettings;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
}

interface WallFramingStrip {
  readonly pathEdge: PolyLine2;
  readonly dirX: number;
  readonly dirY: number;
  readonly leftSide: SolidWallSide;
  readonly rightSide: SolidWallSide;
  readonly leftWallOffset: number;
  readonly rightWallOffset: number;
  readonly plateLumberProfile: LumberProfileDescriptor;
  readonly studLumberProfile: LumberProfileDescriptor;
  readonly isExteriorWall: boolean;
  readonly baseOffset: number;
  readonly wallCondition: WallCondition;
  length: number;
  startExtensionNoClamp: number;
  endExtensionNoClamp: number;
  startExtension: number;
  endExtension: number;
}

interface AddedWallRail {
  readonly rail: PolyLine2;
  readonly leftWallOffset: number;
  readonly rightWallOffset: number;
}

interface FrameWallOptions {
  readonly floorElevation: number;
  readonly ceilingHeight: number;
  readonly wallStrip: WallFramingStrip;
  readonly addedWallRails: AddedWallRail[];
  readonly wallFramingSettings: WallFramingSettings;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly headerFramingSettings: HeaderFramingSettings;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
}

export function wallFramingNew(framings: Framing[], studClippingBSP?: NodeBSP | null): WallFraming {
  return { framings, studClippingBSP };
}

function wallFramingStripNew(
  pathEdge: PolyLine2,
  startExtensionNoClamp: number,
  endExtensionNoClamp: number,
  startExtension: number,
  endExtension: number,
  leftSide: SolidWallSide,
  rightSide: SolidWallSide,
  leftWallOffset: number,
  rightWallOffset: number,
  plateLumberProfile: LumberProfileDescriptor,
  studLumberProfile: LumberProfileDescriptor,
  isExteriorWall: boolean,
  baseOffset: number,
  wallCondition: WallCondition,
): WallFramingStrip {
  const length = polyLine2Length(pathEdge);
  const [dirX, dirY] = polyLine2EdgeTangent(pathEdge, 0);

  return {
    pathEdge,
    startExtensionNoClamp,
    endExtensionNoClamp,
    startExtension,
    endExtension,
    dirX,
    dirY,
    length,
    leftSide,
    rightSide,
    leftWallOffset,
    rightWallOffset,
    plateLumberProfile,
    studLumberProfile,
    isExteriorWall,
    baseOffset,
    wallCondition,
  };
}

function addedWallRailNew(rail: PolyLine2, leftWallOffset: number, rightWallOffset: number): AddedWallRail {
  return {
    rail,
    leftWallOffset,
    rightWallOffset,
  };
}

function wallFramingStripFromSolidWall(
  wall: SolidWall,
  isExteriorWall: boolean,
  wallCatalog: WallCatalogSettings,
): WallFramingStrip {
  const { mass, leftSide, rightSide } = wall;
  const path = getWallPath(wall);
  const { startX, startY, endX, endY, baseOffset, wallCondition } = path;
  const {
    leftOffset: leftWallOffset,
    rightOffset: rightWallOffset,
    leftEdge,
    rightEdge,
    wallAssembly,
  } = solidWallFramingLayer(mass);

  const framingThickness = getWallAssemblyStructuralThickness(wallCatalog.wallMaterials, wallAssembly);

  const plateProfile = getLumberProfile(framingThickness, true);
  const studProfile = getLumberProfile(framingThickness, false);

  const pathEdge = polyLine2ByTwoPoints(startX, startY, endX, endY);
  const [dirX, dirY] = polyLine2EdgeTangent(pathEdge, 0);

  const startExtensionNoClamp = Math.max(
    ray2DotProject(leftEdge[2], leftEdge[3], dirX, dirY, pathEdge[0], pathEdge[1]),
    ray2DotProject(rightEdge[0], rightEdge[1], dirX, dirY, pathEdge[0], pathEdge[1]),
  );

  const endExtensionNoClamp = Math.max(
    ray2DotProject(leftEdge[0], leftEdge[1], -dirX, -dirY, pathEdge[2], pathEdge[3]),
    ray2DotProject(rightEdge[2], rightEdge[3], -dirX, -dirY, pathEdge[2], pathEdge[3]),
  );

  const startExtension = Math.min(Math.max(startExtensionNoClamp, leftWallOffset), plateProfile.width - leftWallOffset);

  const endExtension = Math.min(Math.max(endExtensionNoClamp, leftWallOffset), plateProfile.width - leftWallOffset);

  return wallFramingStripNew(
    pathEdge,
    startExtensionNoClamp,
    endExtensionNoClamp,
    startExtension,
    endExtension,
    wallSideCopy(leftSide),
    wallSideCopy(rightSide),
    leftWallOffset,
    rightWallOffset,
    plateProfile,
    studProfile,
    isExteriorWall,
    baseOffset,
    wallCondition,
  );
}

function mergeStrip(
  prevStrip: WallFramingStrip,
  nextStrip: WallFramingStrip,
  prevIndex: number,
  nextIndex: number,
): WallFramingStrip {
  const {
    pathEdge,
    startExtensionNoClamp,
    endExtensionNoClamp,
    startExtension,
    endExtension,
    leftSide,
    rightSide,
    length,
  } = nextStrip;

  const prevIndex1 = prevIndex + 1;
  const nextIndex1 = nextIndex + 1;

  const extensionNoClamp = nextIndex === 0 ? startExtensionNoClamp : endExtensionNoClamp;
  const extension = nextIndex === 0 ? startExtension : endExtension;
  if (prevIndex === 0) {
    prevStrip.startExtensionNoClamp = extensionNoClamp;
    prevStrip.startExtension = extension;
  } else {
    prevStrip.endExtensionNoClamp = extensionNoClamp;
    prevStrip.endExtension = extension;
  }

  prevStrip.pathEdge[prevIndex] = pathEdge[nextIndex];
  prevStrip.pathEdge[prevIndex1] = pathEdge[nextIndex1];
  prevStrip.length += length;
  prevStrip.leftSide.windows.push(...leftSide.windows);
  prevStrip.rightSide.windows.push(...rightSide.windows);
  prevStrip.leftSide.doors.push(...leftSide.doors);
  prevStrip.rightSide.doors.push(...rightSide.doors);

  return prevStrip;
}

function isValidForStrip(
  prevStrip: WallFramingStrip,
  nextStrip: WallFramingStrip,
  prevIndex: number,
  nextIndex: number,
): boolean {
  const { leftWallOffset, rightWallOffset, pathEdge, dirX, dirY, baseOffset } = nextStrip;
  const {
    leftWallOffset: prevLeftWallOffset,
    rightWallOffset: prevRightWallOffset,
    pathEdge: prevPathEdge,
    dirX: prevDirX,
    dirY: prevDirY,
    baseOffset: prevBaseOffset,
  } = prevStrip;

  const prevIndex1 = prevIndex + 1;
  const nextIndex1 = nextIndex + 1;

  return (
    baseOffset === prevBaseOffset &&
    Math.abs(leftWallOffset - prevLeftWallOffset) < DIST_EPSILON &&
    Math.abs(rightWallOffset - prevRightWallOffset) < DIST_EPSILON &&
    vec2DistSquared(prevPathEdge[prevIndex], prevPathEdge[prevIndex1], pathEdge[nextIndex], pathEdge[nextIndex1]) <
      DIST_EPSILON_SQUARED &&
    Math.abs(vec2Dot(dirX, dirY, prevDirX, prevDirY)) > ONE_MINUS_DIST_EPSILON
  );
}

function mayBeValidForStrip(prevStrip: WallFramingStrip, nextStrip: WallFramingStrip): boolean {
  const { leftWallOffset, rightWallOffset, pathEdge, dirX, dirY } = nextStrip;
  const {
    leftWallOffset: prevLeftWallOffset,
    rightWallOffset: prevRightWallOffset,
    pathEdge: prevPathEdge,
    dirX: prevDirX,
    dirY: prevDirY,
  } = prevStrip;

  return (
    Math.abs(leftWallOffset - prevLeftWallOffset) < DIST_EPSILON &&
    Math.abs(rightWallOffset - prevRightWallOffset) < DIST_EPSILON &&
    Math.abs(vec2Dot(dirX, dirY, prevDirX, prevDirY)) > ONE_MINUS_DIST_EPSILON &&
    line2PerpDistance(prevPathEdge[0], prevPathEdge[1], prevPathEdge[2], prevPathEdge[3], pathEdge[0], pathEdge[1]) <
      DIST_EPSILON
  );
}

function makeExteriorWallStrips(
  options: MakeWallFramingOptions,
  wallStrips: WallFramingStrip[] = [],
): WallFramingStrip[] {
  const { wallSystem, planarGraphPerimeter } = options;
  const { walls, halfEdgeToWall } = wallSystem;

  const { coveredPerimeters } = planarGraphPerimeter;
  const { faceHalfEdges } = coveredPerimeters;

  // iterate perimeter loops
  for (let i = 0, l = faceHalfEdges.length; i < l; i++) {
    const halfEdges = faceHalfEdges[i];
    const loopWallStrips: WallFramingStrip[] = [];
    let currWallStrip: WallFramingStrip | null = null;

    // iterate loop edges
    for (let j = 0, lj = halfEdges.length; j < lj; j++) {
      const wallId = halfEdgeToWall[halfEdges[j]];
      const wall = walls[wallId];

      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      const newStrip = wallFramingStripFromSolidWall(wall, true, options.wallCatalog);

      if (wall.wallType === WallType.SOLID) {
        if (!currWallStrip) {
          currWallStrip = newStrip;
        } else if (isValidForStrip(currWallStrip, newStrip, 0, 2)) {
          mergeStrip(currWallStrip, newStrip, 0, 0);
        } else {
          loopWallStrips.push(currWallStrip);
          currWallStrip = newStrip;
        }
      }
    }

    // add last strip
    if (currWallStrip) {
      loopWallStrips.push(currWallStrip);
    }

    // merge last strip with first
    if (
      loopWallStrips.length >= 2 &&
      isValidForStrip(loopWallStrips[loopWallStrips.length - 1], loopWallStrips[0], 0, 2)
    ) {
      const firstStrip = loopWallStrips[0];
      const lastStrip = loopWallStrips[loopWallStrips.length - 1];

      if (isValidForStrip(lastStrip, firstStrip, 0, 2)) {
        loopWallStrips[0] = mergeStrip(lastStrip, firstStrip, 0, 0);
        loopWallStrips.pop();
      }
    }

    wallStrips.push(...loopWallStrips);
  }

  return wallStrips;
}

function makeInteriorWallStrips(
  options: MakeWallFramingOptions,
  wallStrips: WallFramingStrip[] = [],
): WallFramingStrip[] {
  const { wallSystem } = options;
  const { walls } = wallSystem;

  // collect all the interior walls
  const unconnectedWallStrips: WallFramingStrip[] = [];
  for (const id in walls) {
    const wall = walls[id];
    if (wall.wallType === WallType.SOLID) {
      const path = getWallPath(wall);
      if (path.leftRoomKind === RoomKind.INTERIOR && path.rightRoomKind === RoomKind.INTERIOR) {
        unconnectedWallStrips.push(wallFramingStripFromSolidWall(wall, false, options.wallCatalog));
      }
    }
  }

  // try to connect them
  if (unconnectedWallStrips.length === 0) {
    return wallStrips;
  }
  if (unconnectedWallStrips.length === 1) {
    wallStrips.push(unconnectedWallStrips[0]);
    return wallStrips;
  }

  const joined = new Array<boolean>(unconnectedWallStrips.length);
  joined.fill(false);

  // iterate the uncconected wall segments
  for (let i = 0, l = unconnectedWallStrips.length; i < l; i++) {
    if (joined[i]) {
      continue;
    }

    const refStrip = unconnectedWallStrips[i];
    const { dirX, dirY, pathEdge } = refStrip;
    const originX = pathEdge[0];
    const originY = pathEdge[1];

    // get the segments that are on the same line
    const potentialStripIds: number[] = [i];
    for (let j = 0, lj = unconnectedWallStrips.length; j < lj; j++) {
      if (!joined[j] && i !== j && mayBeValidForStrip(refStrip, unconnectedWallStrips[j])) {
        potentialStripIds.push(j);
      }
    }

    // sort them
    potentialStripIds.sort((a, b) => {
      const { pathEdge: pathA } = unconnectedWallStrips[a];
      const { pathEdge: pathB } = unconnectedWallStrips[b];
      const [aX, aY] = polyLine2EdgeEval(pathA, 0, 0.5, true);
      const [bX, bY] = polyLine2EdgeEval(pathB, 0, 0.5, true);

      return (
        ray2DotProject(originX, originY, dirX, dirY, aX, aY) - ray2DotProject(originX, originY, dirX, dirY, bX, bY)
      );
    });

    // try to merge them
    const firsIndex = potentialStripIds[0];
    joined[firsIndex] = true;
    let a = unconnectedWallStrips[firsIndex];
    for (let j = 1, lj = potentialStripIds.length; j < lj; j++) {
      const index = potentialStripIds[j];
      const b = unconnectedWallStrips[index];
      if (isValidForStrip(a, b, 0, 2)) {
        a = mergeStrip(a, b, 0, 0);
        joined[index] = true;
      } else if (isValidForStrip(a, b, 2, 0)) {
        a = mergeStrip(a, b, 2, 2);
        joined[index] = true;
      } else if (isValidForStrip(a, b, 0, 0)) {
        a = mergeStrip(a, b, 0, 2);
        joined[index] = true;
      } else if (isValidForStrip(a, b, 2, 2)) {
        a = mergeStrip(a, b, 2, 0);
        joined[index] = true;
      }
    }

    wallStrips.push(a);

    if (!joined[i]) {
      wallStrips.push(unconnectedWallStrips[i]);
      joined[i] = true;
    }
  }

  return wallStrips;
}

function splitWallRail(wallRail: PolyLine2, addedWallRails: AddedWallRail[]): PolyLine2[] {
  let splits = [wallRail];
  for (let i = 0, l = addedWallRails.length; i < l; i++) {
    const addedWallRail = addedWallRails[i];
    const { rail, leftWallOffset, rightWallOffset } = addedWallRail;

    const ax = rail[0];
    const ay = rail[1];
    const bx = rail[2];
    const by = rail[3];

    const newSplits: PolyLine2[] = [];
    for (let j = 0, lj = splits.length; j < lj; j++) {
      const currSplit = splits[j];

      const cx = currSplit[0];
      const cy = currSplit[1];
      const dx = currSplit[2];
      const dy = currSplit[3];

      let x1 = cx;
      let y1 = cy;
      let x2 = dx;
      let y2 = dy;

      const intersection = intersectLine2Line2(ax, ay, bx, by, cx, cy, dx, dy);

      if (intersection) {
        if (intersection instanceof CurveCurvePointIntersection) {
          x1 = x2 = intersection.x;
          y1 = y2 = intersection.y;
        } else {
          x1 = intersection.startx;
          y1 = intersection.starty;
          x2 = intersection.endx;
          y2 = intersection.endy;
        }

        const split1 = polyLine2ByTwoPoints(cx, cy, x1, y1);
        const split2 = polyLine2ByTwoPoints(x2, y2, dx, dy);

        const length1 = polyLine2Length(split1);
        const length2 = polyLine2Length(split2);

        const side1 = line2Side(ax, ay, bx, by, cx, cy);
        const side2 = line2Side(ax, ay, bx, by, dx, dy);

        let contraction1 = 0;
        if (side1 !== Line2Side.COLLINEAR) {
          contraction1 = side1 === Line2Side.LEFT ? leftWallOffset : rightWallOffset;
        }

        let contraction2 = 0;
        if (side2 !== Line2Side.COLLINEAR) {
          contraction2 = side2 === Line2Side.LEFT ? leftWallOffset : rightWallOffset;
        }

        if (length1 > contraction1 + DIST_EPSILON) {
          polyLine2ExtendEnd(split1, -contraction1);
          newSplits.push(split1);
        }

        if (length2 > contraction2 + DIST_EPSILON) {
          polyLine2ExtendStart(split2, -contraction2);
          newSplits.push(split2);
        }
      } else {
        newSplits.push(currSplit);
      }
    }

    splits = newSplits;
  }

  return splits;
}

export const emptyWallFramingSettings: WallFramingOpeningSettings = {
  hasCrippleStudEnds: false,
  hasCrippleStudsBelowOpening: false,
  hasCrippleStudsAboveOpening: false,
  hasTopHeaderSillPlate: false,
  numberOfJackStuds: 0,
  numberOfKingStuds: 1,
  headerAssemblyId: undefined,
  roughOpeningBorder: 0,
};

function frameWall({
  addedWallRails,
  floorElevation: levelFloorElevation,
  ceilingHeight,
  wallStrip,
  wallFramingSettings,
  targetedOpeningSettings,
  headerFramingSettings,
  headerAssemblies,
  framingComponents,
}: FrameWallOptions): WallFraming {
  const { hasDoubleTopPlate = true, studDistance = DEFAULT_STUD_DISTANCE, hasBlocking = false } = wallFramingSettings;

  const {
    pathEdge,
    startExtensionNoClamp,
    endExtensionNoClamp,
    startExtension,
    endExtension,
    dirX,
    dirY,
    leftSide,
    rightSide,
    leftWallOffset,
    rightWallOffset,
    plateLumberProfile,
    studLumberProfile,
    isExteriorWall,
    baseOffset,
    wallCondition,
  } = wallStrip;

  const { height: plateThickness } = plateLumberProfile;
  const { height: studThickness } = studLumberProfile;

  const framing: Framing[] = [];
  const leftRoomHandle = leftSide.descriptor?.wallRoom.room?.roomHandle;
  const rightRoomHandle = rightSide.descriptor?.wallRoom.room?.roomHandle;
  const wallRooms: AdjacentRooms = [leftRoomHandle || null, rightRoomHandle || null];

  const wallMeta: WallFramingMeta = {
    adjacentRooms: wallRooms,
    sourceHandle: roomEdgeHandleFromWallSide(leftSide) ?? roomEdgeHandleFromWallSide(rightSide),
    openingHandle: undefined,
    tag: undefined,
    spacing: studDistance,
    wallHeight: ceilingHeight,
    wallCondition,
  };

  // prepare wall rail
  const fullWallRail = polyLine2Copy(pathEdge);
  polyLine2ExtendStart(fullWallRail, isExteriorWall ? startExtension : startExtensionNoClamp);
  polyLine2ExtendEnd(fullWallRail, isExteriorWall ? endExtension : endExtensionNoClamp);

  const bottomPlateCount = 1;
  const topPlateCount = hasDoubleTopPlate ? 2 : 1;
  const wallStripBottom = levelFloorElevation + baseOffset;
  const ceilElevation = levelFloorElevation + ceilingHeight;
  const totalBottomPlateHeight = bottomPlateCount * plateThickness;
  const totalTopPlateHeight = topPlateCount * plateThickness;
  const studBottom = wallStripBottom + totalBottomPlateHeight;
  const studTop = ceilElevation - totalTopPlateHeight;

  // split
  const wallRails = splitWallRail(fullWallRail, addedWallRails);

  for (let i = 0, l = wallRails.length; i < l; i++) {
    const wallRail2 = polyLine2Copy(wallRails[i]);

    polyLine2ExtendStart(wallRail2, -Math.min(LUMBER_UNION_GAP, polyLine2Length(wallRail2)));
    polyLine2ExtendEnd(wallRail2, -Math.min(LUMBER_UNION_GAP, polyLine2Length(wallRail2)));

    const wallRailLength = polyLine2Length(wallRail2);

    if (wallRailLength < DIST_EPSILON) {
      continue;
    }

    const leftEdge = offsetPolyLine2(wallRail2, -leftWallOffset);
    const rightEdge = offsetPolyLine2(wallRail2, rightWallOffset);

    const leftRailStartX = leftEdge[0];
    const leftRailStartY = leftEdge[1];

    // get the windows and doors gaps
    const gaps = getWallGaps({
      leftEdge,
      rightEdge,
      leftSide,
      rightSide,
      isLeftSide: false,
      includeExteriorCasing: false,
      mergeGaps: true,
    });

    const addLumber = (rails: LumberRail[], lumberRail: LumberRail) => {
      rails.push(lumberRail);
      return true;
    };

    const addPlate = (rails: LumberRail[], rail: PolyLine2, elevation: number) => {
      const platePlane = planeNewXY(0, 0, elevation);
      const plateRail = polyLine3OnPlane(rail, platePlane);
      return addLumber(rails, lumberRailNew(plateRail, 0, 0, 1));
    };

    const addBottomPlate = (rails: LumberRail[], startT: number, endT: number, elevation: number) => {
      const bottomPlateStartX = leftRailStartX + dirX * startT;
      const bottomPlateStartY = leftRailStartY + dirY * startT;

      const bottomPlateEndX = leftRailStartX + dirX * endT;
      const bottomPlateEndY = leftRailStartY + dirY * endT;

      const bottomPlateRail = polyLine2ByTwoPoints(
        bottomPlateStartX,
        bottomPlateStartY,
        bottomPlateEndX,
        bottomPlateEndY,
      );

      return addPlate(rails, bottomPlateRail, elevation);
    };

    const addTopPlate = (rails: LumberRail[], elevation: number) => {
      return addPlate(rails, leftEdge, elevation);
    };

    const addSill = (rails: LumberRail[], startT: number, endT: number, elevation: number) => {
      const sillStartX = leftRailStartX + dirX * startT;
      const sillStartY = leftRailStartY + dirY * startT;

      const sillEndX = leftRailStartX + dirX * endT;
      const sillEndY = leftRailStartY + dirY * endT;

      const sillRail = polyLine2ByTwoPoints(sillStartX, sillStartY, sillEndX, sillEndY);

      return addPlate(rails, sillRail, elevation);
    };

    const addBlocking = (rails: LumberRail[], startT: number, endT: number, elevation: number) => {
      if (!hasBlocking) {
        return false;
      }

      if (endT - startT < BLOCKING_MIN_DISTANCE) {
        return false;
      }

      const blockingStartX = leftRailStartX + dirX * startT;
      const blockingStartY = leftRailStartY + dirY * startT;

      const blockingEndX = leftRailStartX + dirX * endT;
      const blockingEndY = leftRailStartY + dirY * endT;

      const blockingRail = polyLine2ByTwoPoints(blockingStartX, blockingStartY, blockingEndX, blockingEndY);

      return addPlate(rails, blockingRail, wallStripBottom + elevation);
    };

    const addHeader = (
      rails: LumberRail[],
      startT: number,
      endT: number,
      elevation: number,
      offset: number,
      railId: string,
    ) => {
      const headerStartX = leftRailStartX + dirX * startT + dirY * offset;
      const headerStartY = leftRailStartY + dirY * startT - dirX * offset;

      const headerEndX = leftRailStartX + dirX * endT + dirY * offset;
      const headerEndY = leftRailStartY + dirY * endT - dirX * offset;

      const headerRail = polyLine3ByArray([headerStartX, headerStartY, elevation, headerEndX, headerEndY, elevation]);

      return addLumber(rails, lumberRailNew(headerRail, -dirY, dirX, 0, undefined, undefined, undefined, railId));
    };

    const addStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
      if (top - bottom < MIN_FRAMING_STUD_LENGTH) {
        return false;
      }

      const posX = leftRailStartX + dirX * t;
      const posY = leftRailStartY + dirY * t;

      const studRail = polyLine3ByArray([posX, posY, top, posX, posY, bottom]);

      return addLumber(rails, lumberRailNew(studRail, dirX, dirY, 0));
    };

    const addStandardStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
      return addStud(rails, t, bottom, top);
    };

    const addKingStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
      return addStud(rails, t, bottom, top);
    };

    const addJackStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
      return addStud(rails, t, bottom, top);
    };

    const addBottomCrippleStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
      return addStud(rails, t, bottom, top);
    };

    const addTopCrippleStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
      return addStud(rails, t, bottom, top);
    };

    // studs
    const lastStudT = wallRailLength - studThickness;

    // create rough openings
    const roughOpenings: RoughOpening[] = gaps.map((gap) => {
      return makeRoughOpening({
        openings: gap.openings,
        gap,
        studThickness,
        wallStripBottom,
        levelFloorElevation,
        targetedOpeningSettings,
        headerAssemblies,
        framingComponents,
        headerFramingSettings,
      });
    });

    let currentOpeningId = 0;
    const openingCount = roughOpenings.length;

    const steps = rangeInclusiveByStep(0, wallRailLength, studDistance);

    let prevStudEndT = 0;
    let prevBlockingElevation = 42;

    for (let j = 0, lj = steps.length; j < lj; j++) {
      const t = steps[j];
      const startT = t;
      const endT = t + studThickness;

      if (endT >= lastStudT) {
        continue;
      }

      while (currentOpeningId < openingCount && roughOpenings[currentOpeningId].extendedEnd < startT) {
        currentOpeningId++;
      }

      let isGap = false;

      if (currentOpeningId < openingCount) {
        const { extendedStart, extendedEnd } = roughOpenings[currentOpeningId];

        isGap = (extendedStart <= startT && startT <= extendedEnd) || (extendedStart <= endT && endT <= extendedEnd);
      }

      const blockingElevation = prevBlockingElevation === 54 ? 42 : 54;

      const blockingStartT = prevStudEndT;
      let blockingEndT = startT;

      if (isGap) {
        const {
          start,
          end,
          bottom,
          top,
          sillPlateCount,
          hasCrippleStudEnds,
          hasCrippleStudsBelowOpening,
          hasCrippleStudsAboveOpening,
          hasTopHeaderSillPlate,
          headerAssemblyId,
          headerProfile,
          numberOfJackStuds,
          adjacentRooms,
          openingHandle,
          resolvedOpeningFraming,
        } = roughOpenings[currentOpeningId];
        const jackStudOffset = numberOfJackStuds * studThickness;
        const headerSillStackHeight = hasTopHeaderSillPlate ? plateThickness : 0;
        const headerEnabled = isOpeningHeaderEnabled(headerAssemblyId);
        const headerStackHeight = headerEnabled ? headerProfile.width : 0;
        const topCrippleBottom = top + headerSillStackHeight + headerStackHeight;
        const crippleStudEndOffset = hasCrippleStudEnds ? studThickness : 0;
        const minBottomT = start + crippleStudEndOffset;
        const maxBottomT = end - crippleStudEndOffset;

        const minTopT = start + jackStudOffset;
        const maxTopT = end - jackStudOffset;

        if (hasCrippleStudsBelowOpening && bottom > studBottom && startT >= minBottomT && endT <= maxBottomT) {
          addWallFramingMember(
            framing,
            WallFramingType.BOTTOM_CRIPPLE_STUD,
            studLumberProfile,
            (studRails) => addBottomCrippleStud(studRails, t, studBottom, bottom - sillPlateCount * plateThickness),
            {
              ...wallMeta,
              adjacentRooms,
              openingHandle,
              resolvedOpeningFraming,
            },
          );
        }

        if (hasCrippleStudsAboveOpening && startT >= minTopT && endT <= maxTopT) {
          addWallFramingMember(
            framing,
            WallFramingType.TOP_CRIPPLE_STUD,
            studLumberProfile,
            (studRails) => addTopCrippleStud(studRails, t, topCrippleBottom, studTop),
            {
              ...wallMeta,
              adjacentRooms,
              openingHandle,
              resolvedOpeningFraming,
            },
          );
        }

        const nextEndT = end + jackStudOffset + studThickness;

        if (nextEndT !== prevStudEndT) {
          blockingEndT = start - jackStudOffset - studThickness;
          addWallFramingMember(
            framing,
            WallFramingType.BLOCKING,
            plateLumberProfile,
            (blockingRails) => addBlocking(blockingRails, blockingStartT, blockingEndT, blockingElevation),
            wallMeta,
          );
          prevBlockingElevation = blockingElevation;
          prevStudEndT = nextEndT;
        }
      } else {
        addWallFramingMember(
          framing,
          WallFramingType.STANDARD_STUD,
          studLumberProfile,
          (studRails) => addStandardStud(studRails, t, studBottom, studTop),
          wallMeta,
        );
        addWallFramingMember(
          framing,
          WallFramingType.BLOCKING,
          plateLumberProfile,
          (blockingRails) => addBlocking(blockingRails, blockingStartT, blockingEndT, blockingElevation),
          wallMeta,
        );
        prevBlockingElevation = blockingElevation;
        prevStudEndT = endT;
      }
    }

    // last blocking piece
    const blockingElevation = prevBlockingElevation === 54 ? 42 : 54;
    addWallFramingMember(
      framing,
      WallFramingType.BLOCKING,
      plateLumberProfile,
      (blockingRails) => addBlocking(blockingRails, prevStudEndT, lastStudT, blockingElevation),
      wallMeta,
    );

    // plates

    const doorRoughOpenings = roughOpenings.filter((x) => x.headerType === OpeningSource.DOOR);
    doorRoughOpenings.sort((a, b) => a.start - b.start);
    const doorOpeningCount = doorRoughOpenings.length;

    for (let j = 0; j < doorOpeningCount + 1; j++) {
      const prevRoughOpening = doorRoughOpenings[j - 1];
      const currRoughOpening = doorRoughOpenings[j];

      const startT = prevRoughOpening ? prevRoughOpening.end : 0;
      const endT = currRoughOpening ? currRoughOpening.start : wallRailLength;

      for (let k = 0; k < bottomPlateCount; k++) {
        addWallFramingMember(
          framing,
          WallFramingType.BOTTOM_PLATE,
          plateLumberProfile,
          (bottomPlateRails) => addBottomPlate(bottomPlateRails, startT, endT, wallStripBottom + k * plateThickness),
          wallMeta,
        );
      }
    }

    for (let j = 0; j < topPlateCount; j++) {
      addWallFramingMember(
        framing,
        WallFramingType.TOP_PLATE,
        plateLumberProfile,
        (topPlateRails) => addTopPlate(topPlateRails, ceilElevation - (j + 1) * plateThickness),
        wallMeta,
      );
    }

    // end stud
    addWallFramingMember(
      framing,
      WallFramingType.STANDARD_STUD,
      studLumberProfile,
      (studRails) => addStandardStud(studRails, wallRailLength - studThickness, studBottom, studTop),
      wallMeta,
    );

    // openings
    for (let j = 0; j < openingCount; j++) {
      const roughOpening = roughOpenings[j];

      const {
        start,
        end,
        bottom,
        top,
        sillPlateCount,
        headerType,
        headerProfile,
        headerAssemblyName,
        headerAssemblyId,
        numberOfKingStuds,
        hasCrippleStudEnds,
        hasTopHeaderSillPlate,
        numberOfJackStuds,
        headerCount,
        adjacentRooms,
        openingHandle,
        resolvedOpeningFraming,
      } = roughOpening;

      const { height: headerThickness } = headerProfile;
      const headerEnabled = isOpeningHeaderEnabled(headerAssemblyId);
      const jackStudOffset = numberOfJackStuds * studThickness;
      const kingStudCount = numberOfKingStuds;

      // king studs
      for (let k = 0; k < kingStudCount; k++) {
        addWallFramingMember(
          framing,
          WallFramingType.KING_STUD,
          studLumberProfile,
          (kingStudRails) =>
            addKingStud(kingStudRails, start - (k + 1) * studThickness - jackStudOffset, studBottom, studTop),
          {
            ...wallMeta,
            adjacentRooms,
            openingHandle,
            resolvedOpeningFraming,
          },
        );
        addWallFramingMember(
          framing,
          WallFramingType.KING_STUD,
          studLumberProfile,
          (kingStudRails) => addKingStud(kingStudRails, end + k * studThickness + jackStudOffset, studBottom, studTop),
          {
            ...wallMeta,
            adjacentRooms,
            openingHandle,
            resolvedOpeningFraming,
          },
        );
      }

      // jack studs
      if (numberOfJackStuds > 0) {
        const jackStudTop = top;
        for (let k = 0; k < numberOfJackStuds; k++) {
          addWallFramingMember(
            framing,
            WallFramingType.JACK_STUD,
            studLumberProfile,
            (jackStudRails) =>
              addJackStud(jackStudRails, start - jackStudOffset + k * studThickness, studBottom, jackStudTop),
            {
              ...wallMeta,
              adjacentRooms,
              openingHandle,
              resolvedOpeningFraming,
            },
          );
          addWallFramingMember(
            framing,
            WallFramingType.JACK_STUD,
            studLumberProfile,
            (jackStudRails) =>
              addJackStud(jackStudRails, end + jackStudOffset - (k + 1) * studThickness, studBottom, jackStudTop),
            {
              ...wallMeta,
              adjacentRooms,
              openingHandle,
              resolvedOpeningFraming,
            },
          );
        }
      }
      const headerSillStackHeight = hasTopHeaderSillPlate ? plateThickness : 0;
      const headerElevation = top + headerSillStackHeight;

      if (hasTopHeaderSillPlate) {
        addWallFramingMember(
          framing,
          WallFramingType.TOP_SILL_PLATE,
          plateLumberProfile,
          (headerSillRails) => addSill(headerSillRails, start - jackStudOffset, end + jackStudOffset, top),
          {
            ...wallMeta,
            adjacentRooms,
            openingHandle,
            resolvedOpeningFraming,
          },
        );
      }

      if (headerEnabled) {
        // add header
        addWallFramingMember(
          framing,
          WallFramingType.HEADER,
          headerProfile,
          (headerRails) => {
            const headerRailGroupId = uid();
            for (let h = 0; h < headerCount; h++) {
              addHeader(
                headerRails,
                start - jackStudOffset,
                end + jackStudOffset,
                headerElevation,
                plateLumberProfile.width - headerThickness * h,
                headerRailGroupId,
              );
            }
            return headerCount > 0;
          },
          {
            ...wallMeta,
            adjacentRooms,
            openingHandle,
            source: headerType,
            headerAssemblyName,
            headerAssemblyId,
            resolvedOpeningFraming,
          },
        );
      }

      if (bottom > studBottom) {
        const sillElevation = bottom - sillPlateCount * plateThickness;

        // side cripple studs
        if (hasCrippleStudEnds) {
          addWallFramingMember(
            framing,
            WallFramingType.BOTTOM_CRIPPLE_STUD,
            studLumberProfile,
            (bottomCrippleStudRails) => addBottomCrippleStud(bottomCrippleStudRails, start, studBottom, sillElevation),
            {
              ...wallMeta,
              adjacentRooms,
              openingHandle,
              resolvedOpeningFraming,
            },
          );
          addWallFramingMember(
            framing,
            WallFramingType.BOTTOM_CRIPPLE_STUD,
            studLumberProfile,
            (bottomCrippleStudRails) =>
              addBottomCrippleStud(bottomCrippleStudRails, end - studThickness, studBottom, sillElevation),
            {
              ...wallMeta,
              adjacentRooms,
              openingHandle,
              resolvedOpeningFraming,
            },
          );
        }

        // sill
        for (let s = 0; s < sillPlateCount; s++) {
          addWallFramingMember(
            framing,
            WallFramingType.BOTTOM_SILL_PLATE,
            plateLumberProfile,
            (sillRails) => addSill(sillRails, start, end, sillElevation + s * plateThickness),
            wallMeta,
          );
        }
      }
    }

    addedWallRails.push(addedWallRailNew(wallRail2, leftWallOffset, rightWallOffset));
  }

  return wallFramingNew(framing, null);
}

// Need to update this as well

export function makeWallFraming(options: MakeWallFramingOptions): WallFraming[] {
  const {
    level,
    wallFramingSettings,
    targetedOpeningSettings,
    headerFramingSettings,
    headerAssemblies,
    framingComponents,
  } = options;
  const { floorElevation, topOfWall } = level;

  const wallFraming: WallFraming[] = [];
  const addedWallRails: AddedWallRail[] = [];

  // helper function
  const frameWalls = (wallStrips: WallFramingStrip[]) => {
    for (let i = 0, l = wallStrips.length; i < l; i++) {
      const wallStrip = wallStrips[i];
      wallFraming.push(
        frameWall({
          floorElevation,
          ceilingHeight: topOfWall,
          wallStrip,
          wallFramingSettings,
          addedWallRails,
          targetedOpeningSettings,
          headerFramingSettings,
          headerAssemblies,
          framingComponents,
        }),
      );
    }
  };

  // build exterior wall strips
  const exteriorWallStrips = makeExteriorWallStrips(options);

  // look for the longest wall to the get the main axis
  let maxLength = Number.NEGATIVE_INFINITY;
  let mainDirX = 1;
  let mainDirY = 0;
  for (let i = 0, l = exteriorWallStrips.length; i < l; i++) {
    const wallStip = exteriorWallStrips[i];
    const { length } = wallStip;

    if (length > maxLength) {
      const { dirX, dirY } = wallStip;
      maxLength = length;
      mainDirX = dirX;
      mainDirY = dirY;
    }
  }

  // sort function
  const exteriorWallFramingStripCompare = (a: WallFramingStrip, b: WallFramingStrip): number => {
    const dotA = Math.abs(vec2Dot(mainDirX, mainDirY, a.dirX, a.dirY));
    const dotB = Math.abs(vec2Dot(mainDirX, mainDirY, b.dirX, b.dirY));

    if (Math.abs(dotA - dotB) > DIST_EPSILON) {
      return dotB - dotA;
    }

    return b.length - a.length;
  };

  // sort the wall strips
  exteriorWallStrips.sort(exteriorWallFramingStripCompare);

  // build interior wall strips
  const interiorWallStrips = makeInteriorWallStrips(options);

  // sort function
  const interiorWallFramingStripCompare = (a: WallFramingStrip, b: WallFramingStrip): number => {
    return b.length - a.length;
  };

  // sort the wall strips
  interiorWallStrips.sort(interiorWallFramingStripCompare);

  // frame walls
  frameWalls(exteriorWallStrips);
  frameWalls(interiorWallStrips);

  return wallFraming;
}
